<template>
  <i-modal v-if="!confirm" v-model:show="isOpen" :style="{maxWidth: '660px'}" @openAutoFocus="checkRestrictions">
    <template #header>
      <div class="flex justify-between">
        <span class="title flex-auto">{{ title }}</span>
        <i-button title="Close" class="i-button-close" @click.prevent="cancelPaste()">
          <i-icon icon="times" size="medium"></i-icon>
        </i-button>
      </div>
    </template>

    <template #default>
      <div class="loading" v-if="evaluating">
        <Loading/>
      </div>
      <div class="copying" v-else-if="error">
        <div class="copying-title">
          <i-icon icon="exclamation-triangle" size="large" class="title-icon yellow"></i-icon>
          <h1>{{ LANG.copyingNotAllowed }}</h1>
        </div>
        <div>
          <h2>{{ message }}</h2>
        </div>
      </div>
      <div class="copying" v-else>
        <div class="copying-title">
          <i-icon type="far" icon="copy" size="large" class="title-icon"></i-icon>
          <h1 v-if="pasting">{{ `${LANG.copying} ... (${copiedDocumentsCount}/${documentsToCopy})` }}</h1>
          <h1 v-else-if="finished && copiedDocumentsCount === documentsToCopy">{{ `${LANG.copied} (${copiedDocumentsCount}/${documentsToCopy})` }}</h1>
          <h1 v-else>{{ `${LANG.copy} ${validDocuments} of ${totalDocuments}` }}</h1>
        </div>
        <div class="documents-wrapper">
          <div v-for='(document, key, idx) in documentsStatus'
            class="document-entry"
            :key="document.nodeId">
            <DocumentThumbnail
              :thumbnail="document.preview"
              :title="documentTitle(document.title)"
              :alt="documentTitle(document.title)"
              class="document-preview"
            />
            <div class="document-info">
              <span class="document-title">{{ `${idx+1} ${documentTitle(document.title)}` }}</span>
              <span class="document-status"><span v-if="document.status === DOCUMENT_STATUS.WARNING" class="yellow">{{ LANG.notAllowed }} | </span>{{ document.message }}</span>
            </div>
            <div class="document-status-circle">
              <i-icon :class="statusClass(document.status)" :icon="statusIcon(document.status)" size="large"></i-icon>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-center gap-1">
        <i-button
          variant="secondary"
          :disabled="pasting"
          @click.prevent="cancelPaste()"
        >
          {{ (finished || error) ? LANG.close : LANG.cancel }}
        </i-button>
        <i-button
          v-if="!error && !finished"
          :disabled="pasting"
          variant="secondary"
          @click.prevent="() => {clearDocuments(); $emit('update:show', false);}"
        >
          {{ LANG.clearAll }}
        </i-button>
        <i-button
          v-if="!error && !finished"
          :disabled="!enabledPasting || pasting"
          variant="primary"
          @click.prevent="pasteDocuments()"
        >
          {{ LANG.continue }}
        </i-button>
      </div>
    </template>
  </i-modal>
  <i-modal v-else :title="LANG.cancelPasting" :style="{maxWidth: '660px'}" v-model:show="confirm">
      {{ LANG.confirmCancelPasting }}
      <template #footer>
        <i-button variant="secondary" @click.prevent="confirm=false; pasting = false;">{{ LANG.back }}</i-button>
        <i-button variant="primary" @click.prevent="confirm=false; $emit('update:show', false)">{{ LANG.confirm }}</i-button>
      </template>
    </i-modal>
</template>

<script>
import { defineComponent } from 'vue';
import { mapBrowserGetters, mapBrowserMutations } from '@/store';
import { mapState, mapMutations } from 'vuex';
import fetchService from '@/utils/model/fetch_service';
import DocumentThumbnail from '@/components/browser/documents/DocumentThumbnail.vue';
import Loading from '@/components/common/Loading.vue';
import globals from '@/globals';

const SessionId = globals.I.settings.session.id;

const DOCUMENT_STATUS = {
  IDLE: '0',
  WARNING: '1',
  ERROR: '2',
  SUCCESS: '3',
  LOADING: '4',
};

export default defineComponent({
  props: ['show'],
  components: {
    DocumentThumbnail,
    Loading,
  },
  data: () => ({
    error: false,
    message: '',
    documentsStatus: {},
    evaluating: true,
    pasting: false,
    finished: false,
    copiedDocumentsCount: 0,
    documentsToCopy: 0,
    confirm: false,
  }),
  computed: {
    ...mapState(['LANG', 'browserMode']),
    ...mapState('clipboard', {
      copiedDocuments: (clipboard) => clipboard[SessionId].documents,
    }),
    ...mapBrowserGetters([
      'selectedTreeNode',
    ]),
    enabledPasting() {
      return !this.error && this.validDocuments > 0;
    },
    totalDocuments() {
      return Object.keys(this.documentsStatus).length;
    },
    validDocuments() {
      if (!Object.keys(this.documentsStatus).length) return 0;

      return Object.values(this.documentsStatus).filter((d) => d.status === DOCUMENT_STATUS.IDLE).length;
    },
    DOCUMENT_STATUS() {
      return DOCUMENT_STATUS;
    },
    isOpen: {
      get() {
        return this.show;
      },
      set(value) {
        if (this.confirm || !this.enabledPasting) {
          this.$emit('update:show', value);
        } else {
          this.confirm = true;
        }
      },
    },
  },
  methods: {
    ...mapBrowserMutations([
      'setEntityTableDirty',
    ]),
    ...mapMutations('clipboard', ['clearDocuments', 'removeDocument']),
    async checkRestrictions() {
      this.finished = false;
      this.pasting = false;
      this.evaluating = true;
      this.documentsStatus = {};
      this.copiedDocuments.forEach((c) => { this.documentsStatus[c.nodeId] = c; });

      const response = await fetchService.GetData('docbrowser_paste_restrictions', {
        __imperia_target_category: this.selectedTreeNode,
        __imperia_document_ids: this.copiedDocuments.map((c) => c.nodeId),
      });

      this.error = response.error;
      this.message = response.message;

      this.evaluating = false;

      if (this.error) return;

      response.data.forEach((d) => {
        this.documentsStatus[d.node_id].status = d.status;
        this.documentsStatus[d.node_id].message = d.message;
      });
    },
    async pasteDocuments() {
      this.pasting = true;
      this.copiedDocumentsCount = 0;
      this.documentsToCopy = this.validDocuments;

      const documents = Object.values(this.documentsStatus);
      await documents.reduce(async (promise, doc) => {
        await promise;

        if (this.confirm || !this.isOpen) {
          Promise.resolve();
          return false;
        }

        await this.pasteDoc(doc);
        return true;
      }, Promise.resolve());

      this.setEntityTableDirty();
    },
    async pasteDoc(doc) {
      this.removeDocument(doc);
      // FIXME: status mapping
      if (doc.status !== DOCUMENT_STATUS.IDLE) {
        return;
      }

      this.documentsStatus[doc.nodeId].status = DOCUMENT_STATUS.LOADING;

      const response = await fetchService.GetData('docbrowser_paste_documents', {
        __imperia_target_category: this.selectedTreeNode,
        __imperia_document_ids: doc.nodeId,
      });

      // FIXME: status mapping
      this.documentsStatus[doc.nodeId].status = response.error ? DOCUMENT_STATUS.ERROR : DOCUMENT_STATUS.SUCCESS;
      this.documentsStatus[doc.nodeId].message = response.message;
      this.copiedDocumentsCount += 1;

      if (this.copiedDocumentsCount === this.documentsToCopy) {
        this.pasting = false;
        this.finished = true;
      }
    },
    cancelPaste() {
      if (this.error || this.finished) {
        this.$emit('update:show', false);
        return;
      }

      this.confirm = true;
    },
    statusIcon(status) {
      return {
        [DOCUMENT_STATUS.IDLE]: 'circle',
        [DOCUMENT_STATUS.WARNING]: 'exclamation-triangle',
        [DOCUMENT_STATUS.ERROR]: 'exclamation-circle',
        [DOCUMENT_STATUS.LOADING]: 'circle',
        [DOCUMENT_STATUS.SUCCESS]: 'check-circle',
      }[status];
    },
    statusClass(status) {
      return {
        [DOCUMENT_STATUS.IDLE]: '',
        [DOCUMENT_STATUS.WARNING]: 'yellow',
        [DOCUMENT_STATUS.ERROR]: 'red',
        [DOCUMENT_STATUS.LOADING]: 'loader',
        [DOCUMENT_STATUS.SUCCESS]: 'green',
      }[status];
    },
    documentTitle(title) {
      return title || 'No title';
    },
  },
});
</script>

<style scoped lang="scss">
.loading {
  height: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.copying {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  .copying-title {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .title-icon {
      font-size: 48px;
    }
  }

  .documents-wrapper {
    .document-entry {
      display: flex;
      margin-top: 25px;

      .document-preview {
        border: 1px solid $gray;
        border-radius: 2px;
        align-self: center;
        height: 46px;
      }
      .document-info {
        display: flex;
        flex-direction: column;
        margin-left: 8px;
        min-width: 350px;
        width: 80%;

        .document-title {
          margin-top: 5px;
          font-weight: bold;
        }

        .document-status {
          color: $gray;
          font-size: 12px;
          margin-top: 5px;
        }
      }

      .document-status-circle {
        color: $gray;
        align-self: center;
        padding: 0 25px 0 25px;

        .loader {
          border: 2px solid transparent;
          border-top: 2px solid $blue;
          border-radius: 50%;
          animation: spin 2s linear infinite;
          margin: 2px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      }
    }
  }
}

</style>
